import { formatNumber, numberWithCommas } from "../../utils";
import { BdcLineNo, BdcListShape, BdcShipmentKind } from "../../types/bdc.type";
import { NO_STORES_LABEL } from "./constants";

const TH_MONTH_SHORT = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];
const TH_MONTH_LONG = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

/** Split a bare `YYYY-MM-DD` (or the date part of an ISO instant) without any time zone shift. */
const dateParts = (value: string | null | undefined) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, y, m, d] = match;
  return { year: Number(y), month: Number(m) - 1, day: Number(d) };
};

/** `2026-09-18` → `18 ก.ย. 2569` (Buddhist era, four-digit year). */
export const thaiDate = (value: string | null | undefined): string => {
  const p = dateParts(value);
  if (!p) return value ?? "-";
  return `${p.day} ${TH_MONTH_SHORT[p.month]} ${p.year + 543}`;
};

/** `2026-09-18` → `18 กันยายน 2569` — the long form printed on the A4. */
export const thaiLongDate = (value: string | null | undefined): string => {
  const p = dateParts(value);
  if (!p) return value ?? "-";
  return `${p.day} ${TH_MONTH_LONG[p.month]} ${p.year + 543}`;
};

/** An instant in Bangkok, Buddhist era, 24h: `18 ก.ย. 2569 06:10`. */
export const thaiDateTime = (value: string | null | undefined): string => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((x) => x.type === type)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${thaiDate(`${get("year")}-${get("month")}-${get("day")}`)} ${hour}:${get("minute")}`;
};

/** Today's date on the Bangkok calendar as `YYYY-MM-DD`. */
export const todayBkk = (): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok" }).format(new Date());

export const pcs = (value: number | null | undefined) => numberWithCommas(value ?? 0);
export const baht = (value: number | null | undefined) => formatNumber(value ?? 0);

/** รอบ 1 / ไม่มีรายร้าน */
export const roundLabel = (kind: BdcShipmentKind, round: number | null | undefined) =>
  kind === "X" ? NO_STORES_LABEL : round == null ? "-" : `รอบ ${round}`;

/** Read a list that may be `{ rows }` or a bare array. */
export const rowsOf = <T,>(data: BdcListShape<T> | undefined): T[] => {
  if (!data) return [];
  return Array.isArray(data) ? data : data.rows ?? [];
};

/** Numeric-aware compare for สาย / จุดส่ง, which the file pads with zeros. */
export const compareLineNo = (a: BdcLineNo, b: BdcLineNo): number => {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return String(a).localeCompare(String(b));
};

/** Display สาย / จุดส่ง without the file's leading zeros (`00012` → `12`). */
export const lineText = (value: BdcLineNo | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
};

/** Shorten a 7-11 product name for a narrow column head: drop ขนมปัง/ขนม prefix and the GB tail. */
export const shortSkuName = (name: string): string =>
  name
    .replace(/^ขนมปัง/, "")
    .replace(/^ขนม/, "")
    .replace(/_?\s*GB$/, "")
    .replace(/_$/, "")
    .trim() || name;
